import { motion } from "framer-motion";
import {
  BookOpen,
  ChevronLeft,
  ClipboardList,
  GraduationCap,
  LayoutDashboard,
  Layers,
  Library,
  LineChart,
  Settings,
  Sparkles,
  Target,
} from "lucide-react";
import { useWorkspace, type WorkspaceView } from "@/stores/workspace";
import { cn } from "@/lib/cn";

interface NavItem {
  view: WorkspaceView;
  label: string;
  icon: typeof BookOpen;
  hint?: string;
}

const GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Workspace",
    items: [
      { view: "learn", label: "Learn", icon: BookOpen, hint: "Daily feed" },
      { view: "dashboard", label: "Dashboard", icon: LayoutDashboard, hint: "Your progress" },
      { view: "tutor", label: "Tutor", icon: Sparkles, hint: "Talk to Maya" },
      { view: "practice", label: "Practice", icon: Target, hint: "Adaptive drills" },
      { view: "exam", label: "Mock exams", icon: ClipboardList, hint: "Agentic mocks" },
    ],
  },
  {
    title: "Study",
    items: [
      { view: "topics", label: "Topics", icon: GraduationCap },
      { view: "flashcards", label: "Flashcards", icon: Layers },
      { view: "score", label: "Score", icon: LineChart },
    ],
  },
  {
    title: "More",
    items: [
      { view: "resources", label: "Library", icon: Library },
      { view: "settings", label: "Settings", icon: Settings },
    ],
  },
];

export function LeftRail({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) {
  const view = useWorkspace((s) => s.view);
  const setView = useWorkspace((s) => s.setView);

  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col border-r border-border/60 bg-card/40 backdrop-blur-sm transition-[width] duration-300 md:flex",
        expanded ? "w-56" : "w-[68px]",
      )}
    >
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-5">
        {GROUPS.map((group) => (
          <div key={group.title}>
            {expanded && (
              <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                {group.title}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = view === item.view;
                return (
                  <button
                    key={item.view}
                    type="button"
                    onClick={() => setView(item.view)}
                    title={!expanded ? item.label : undefined}
                    className={cn(
                      "group relative flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground",
                      isActive && "text-foreground",
                      !expanded && "justify-center",
                    )}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="rail-active"
                        className="absolute inset-0 -z-10 rounded-xl bg-secondary"
                        transition={{ type: "spring", stiffness: 500, damping: 34 }}
                      />
                    )}
                    <item.icon className="size-[18px] shrink-0" />
                    {expanded && <span className="flex-1 truncate text-left">{item.label}</span>}
                    {expanded && item.hint && isActive && (
                      <span className="text-[10px] text-muted-foreground">{item.hint}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <button
        type="button"
        onClick={onToggle}
        className="m-3 flex items-center justify-center gap-2 rounded-xl border border-border/60 py-2 text-xs text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
      >
        <ChevronLeft className={cn("size-4 transition-transform", !expanded && "rotate-180")} />
        {expanded && <span>Collapse</span>}
      </button>
    </aside>
  );
}
